import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import {
  clienteVendasApi,
  ListClienteVendasParams,
} from '../../services/api/cliente-vendas.api';
import {
  ClienteVendaDetail,
  ClienteVendaListItem,
  ClienteVendasFilters,
  PaginatedClienteVendas,
} from '../../types/cliente-venda';

export interface ClienteVendasState {
  items: ClienteVendaListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  filters: ClienteVendasFilters;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  currentClienteId: string | null;
  selectedDetail: ClienteVendaDetail | null;
  detailStatus: 'idle' | 'loading' | 'error';
  detailError: string | null;
}

const initialState: ClienteVendasState = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  filters: {},
  status: 'idle',
  error: null,
  currentClienteId: null,
  selectedDetail: null,
  detailStatus: 'idle',
  detailError: null,
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.response?.status === 403)
      return 'Você não tem permissão para acessar este recurso.';
    if (error.code === 'ERR_NETWORK')
      return 'Não foi possível conectar ao servidor.';
  }
  return fallback;
};

export const fetchClienteVendasThunk = createAsyncThunk<
  PaginatedClienteVendas,
  { clienteId: string; params: ListClienteVendasParams },
  { rejectValue: string }
>('clienteVendas/fetch', async ({ clienteId, params }, { rejectWithValue }) => {
  try {
    return await clienteVendasApi.listByCliente(clienteId, params);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar vendas'));
  }
});

export const fetchVendaDetailThunk = createAsyncThunk<
  ClienteVendaDetail,
  string,
  { rejectValue: string }
>('clienteVendas/fetchDetail', async (id, { rejectWithValue }) => {
  try {
    return await clienteVendasApi.detail(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar venda'));
  }
});

const clienteVendasSlice = createSlice({
  name: 'clienteVendas',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<ClienteVendasFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = {};
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
    clearSelectedDetail(state) {
      state.selectedDetail = null;
      state.detailStatus = 'idle';
      state.detailError = null;
    },
    setCurrentClienteId(state, action: PayloadAction<string | null>) {
      if (state.currentClienteId !== action.payload) {
        state.items = [];
        state.page = 1;
        state.total = 0;
        state.totalPages = 0;
        state.filters = {};
        state.status = 'idle';
        state.error = null;
        state.selectedDetail = null;
        state.detailStatus = 'idle';
        state.detailError = null;
      }
      state.currentClienteId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClienteVendasThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClienteVendasThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.data;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchClienteVendasThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar vendas';
      })

      .addCase(fetchVendaDetailThunk.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchVendaDetailThunk.fulfilled, (state, action) => {
        state.detailStatus = 'idle';
        state.selectedDetail = action.payload;
      })
      .addCase(fetchVendaDetailThunk.rejected, (state, action) => {
        state.detailStatus = 'error';
        state.detailError = action.payload ?? 'Erro ao carregar venda';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setLimit,
  clearSelectedDetail,
  setCurrentClienteId,
} = clienteVendasSlice.actions;

export default clienteVendasSlice.reducer;
